import { useState } from 'react';
import { DeleteOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type {
  MagazineArticleAdminPage as PaginaDeArtigos,
  MagazineIssueAdmin as EdicaoDoAdmin,
  MagazineIssueAdminPage as PaginaDeEdicoes,
  MagazineIssueInput as EntradaDaEdicao,
} from '@titularis/api-client';
import { api } from '../api';
import { formatarData } from '../dominio';
import { FormularioDoArtigo } from '../components/FormularioDoArtigo';
import { EnvioDeArquivo } from '../components/EnvioDeArquivo';
import {
  SECAO_LABEL,
  SITUACAO_LABEL,
  SITUACAO_COR,
  situacaoDaEdicao,
  situacaoDoArtigo,
  todasAsEdicoes,
  type SituacaoDoArtigo,
} from './revista/artigo';

/**
 * Duas abas na mesma mesa: a edição organiza o conjunto, o artigo guarda o
 * trabalho de cada autor. Separá-las em telas esconderia que as duas datas
 * precisam vencer para o artigo aparecer. A edição cabe num Modal; o artigo
 * abre em Drawer porque autores e referências fazem o formulário crescer.
 *
 * Não há botão publicar. A data é a publicação nos dois níveis: vazia mantém
 * rascunho, futura agenda, passada libera. "Aguarda edição" é o artigo cuja
 * data já venceu dentro de uma edição ainda em preparação ou agendada. Dizer
 * "No ar" olhando só para ele prometeria um link que a API ainda recusa.
 * A etiqueta explica o cadastro; quem decide a entrega continua sendo a API.
 */

type ArtigoDaLista = PaginaDeArtigos['items'][number];
type EdicaoDaLista = PaginaDeEdicoes['items'][number];

function EtiquetaDeSituacao({ situacao }: { situacao: SituacaoDoArtigo }) {
  return <Tag color={SITUACAO_COR[situacao]}>{SITUACAO_LABEL[situacao]}</Tag>;
}

export function RevistaPage() {
  const { token } = theme.useToken();
  const { message: mensagem } = App.useApp();
  const consultas = useQueryClient();
  const [aba, setAba] = useState('edicoes');
  const [edicaoEmFormulario, setEdicaoEmFormulario] = useState<EdicaoDoAdmin | null | undefined>();
  const [artigoEmFormulario, setArtigoEmFormulario] = useState<string | null | undefined>();
  const [pagina, setPagina] = useState(1);
  const [tamanhoDaPagina, setTamanhoDaPagina] = useState(20);

  const edicoes = useQuery({
    queryKey: ['revista', 'edicoes', 'todas'],
    queryFn: () => todasAsEdicoes(api.listMagazineIssuesAll),
  });
  const artigos = useQuery({
    queryKey: ['revista', 'artigos', pagina, tamanhoDaPagina],
    queryFn: () => api.listMagazineArticleDrafts(pagina, tamanhoDaPagina),
    enabled: aba === 'artigos',
  });
  const apagarEdicao = useMutation({
    mutationFn: (id: string) => api.deleteMagazineIssue(id),
    onSuccess: async () => {
      mensagem.success('Edição apagada.');
      await consultas.invalidateQueries({ queryKey: ['revista'] });
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const apagarArtigo = useMutation({
    mutationFn: (id: string) => api.deleteMagazineArticle(id),
    onSuccess: async () => {
      mensagem.success('Artigo apagado.');
      // A última linha de uma página pode ser apagada. Recuar evita deixar
      // a redação numa página vazia enquanto ainda há artigos na anterior.
      if (artigos.data?.items.length === 1 && pagina > 1) setPagina(pagina - 1);
      await consultas.invalidateQueries({ queryKey: ['revista'] });
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const edicaoPorId = new Map(edicoes.data?.map((edicao) => [edicao.id, edicao]));

  return (
    <Space
      orientation="vertical"
      size={token.marginMD}
      style={{ width: '100%' }}
    >
      <Flex
        justify="space-between"
        align="flex-start"
        gap={token.marginSM}
        wrap
      >
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Revista Titularis
          </Typography.Title>
          <Typography.Text type="secondary">
            Edições e artigos da revista. A data de publicação põe o conteúdo no ar.
          </Typography.Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={aba === 'artigos' && (!edicoes.data?.length || edicoes.isError)}
          onClick={() => aba === 'edicoes'
            ? setEdicaoEmFormulario(null)
            : setArtigoEmFormulario(null)}
        >
          {aba === 'edicoes' ? 'Nova edição' : 'Novo artigo'}
        </Button>
      </Flex>
      {edicoes.error && (
        <Alert
          type="error"
          showIcon
          title={edicoes.error.message}
          action={
            <Button onClick={() => void edicoes.refetch()}>Tentar novamente</Button>
          }
        />
      )}
      {artigos.error && (
        <Alert
          type="error"
          showIcon
          title={artigos.error.message}
          action={
            <Button onClick={() => void artigos.refetch()}>Tentar novamente</Button>
          }
        />
      )}
      {aba === 'artigos' && edicoes.isSuccess && !edicoes.data.length && (
        <Alert
          type="info"
          showIcon
          title="Cadastre uma edição antes de acrescentar artigos."
        />
      )}
      <Tabs
        activeKey={aba}
        onChange={setAba}
        items={[
          {
            key: 'edicoes',
            label: 'Edições',
            children: (
              <Card styles={{ body: { padding: 0 } }}>
                <Table<EdicaoDaLista>
                  rowKey="id"
                  loading={edicoes.isPending}
                  dataSource={edicoes.data}
                  pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                  }}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: <Empty description="Nenhuma edição cadastrada." /> }}
                  columns={[
                    {
                      title: 'Rótulo',
                      dataIndex: 'label',
                      render: (rotulo: string, linha) => (
                        <Typography.Link onClick={() => setEdicaoEmFormulario(linha)}>
                          {rotulo}
                        </Typography.Link>
                      ),
                    },
                    {
                      title: 'Situação',
                      render: (_, linha) => (
                        <EtiquetaDeSituacao situacao={situacaoDaEdicao(linha.publishedAt)} />
                      ),
                    },
                    {
                      title: 'Publicação',
                      dataIndex: 'publishedAt',
                      render: (valor: string | null) => formatarData(valor),
                    },
                    {
                      title: 'Artigos',
                      dataIndex: 'totalDeArtigos',
                      align: 'right',
                    },
                    {
                      title: 'Capa',
                      dataIndex: 'temCapa',
                      align: 'center',
                      render: (temCapa: boolean) => temCapa ? (
                        <Tooltip title="Tem capa"><PictureOutlined /></Tooltip>
                      ) : '-',
                    },
                    {
                      title: 'Ações',
                      render: (_, linha) => (
                        <Popconfirm
                          title="Apagar esta edição?"
                          description="A capa também será apagada."
                          okText="Apagar"
                          cancelText="Cancelar"
                          onConfirm={() => apagarEdicao.mutate(linha.id)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={`Apagar ${linha.label}`}
                            loading={apagarEdicao.isPending && apagarEdicao.variables === linha.id}
                          />
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'artigos',
            label: 'Artigos',
            children: (
              <Card styles={{ body: { padding: 0 } }}>
                <Table<ArtigoDaLista>
                  rowKey="id"
                  loading={artigos.isPending}
                  dataSource={artigos.data?.items}
                  pagination={{
                    current: pagina,
                    pageSize: tamanhoDaPagina,
                    total: artigos.data?.total ?? 0,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 60],
                    // Sem `hideOnSinglePage`: ele levaria junto o seletor de tamanho.
                    onChange: (proxima, tamanho) => {
                      setPagina(tamanho === tamanhoDaPagina ? proxima : 1);
                      setTamanhoDaPagina(tamanho);
                    },
                    // O cartão não tem recuo, e sem este o seletor encostava na borda dele.
                    style: { marginInline: token.padding },
                  }}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: <Empty description="Nenhum artigo cadastrado." /> }}
                  columns={[
                    {
                      title: 'Título',
                      dataIndex: 'title',
                      render: (titulo: string, linha) => (
                        <Typography.Link onClick={() => setArtigoEmFormulario(linha.id)}>
                          {titulo}
                        </Typography.Link>
                      ),
                    },
                    {
                      title: 'Edição',
                      render: (_, linha) => edicaoPorId.get(linha.issueId)?.label ?? '-',
                    },
                    {
                      title: 'Seção',
                      render: (_, linha) => SECAO_LABEL[linha.section],
                    },
                    {
                      title: 'Situação',
                      render: (_, linha) => {
                        const edicao = edicaoPorId.get(linha.issueId);
                        return edicao ? (
                          <EtiquetaDeSituacao
                            situacao={situacaoDoArtigo(linha.publishedAt, edicao.publishedAt)}
                          />
                        ) : '-';
                      },
                    },
                    {
                      title: 'Publicação',
                      dataIndex: 'publishedAt',
                      render: (valor: string | null) => formatarData(valor),
                    },
                    {
                      title: 'Autores',
                      render: (_, linha) => linha.authors.map((autor) => autor.name).join(', '),
                    },
                    {
                      title: 'Downloads',
                      dataIndex: 'totalDeDownloads',
                      align: 'right',
                    },
                    {
                      title: 'Ações',
                      render: (_, linha) => (
                        <Popconfirm
                          title="Apagar este artigo?"
                          description="A capa, o PDF e os registros de uso serão apagados."
                          okText="Apagar"
                          cancelText="Cancelar"
                          onConfirm={() => apagarArtigo.mutate(linha.id)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={`Apagar ${linha.title}`}
                            loading={apagarArtigo.isPending && apagarArtigo.variables === linha.id}
                          />
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
      {edicaoEmFormulario !== undefined && (
        <FormularioDaEdicao
          edicao={edicaoEmFormulario}
          onFechar={() => setEdicaoEmFormulario(undefined)}
        />
      )}
      {artigoEmFormulario !== undefined && (
        <FormularioDoArtigo
          artigoId={artigoEmFormulario}
          edicoes={edicoes.data ?? []}
          onFechar={() => setArtigoEmFormulario(undefined)}
        />
      )}
    </Space>
  );
}

type ValoresDaEdicao = Omit<EntradaDaEdicao, 'publishedAt'> & {
  publishedAt?: Dayjs | null;
};

function FormularioDaEdicao({
  edicao,
  onFechar,
}: {
  edicao: EdicaoDoAdmin | null;
  onFechar: () => void;
}) {
  const { message: mensagem } = App.useApp();
  const consultas = useQueryClient();
  const [formulario] = Form.useForm<ValoresDaEdicao>();
  const [salvo, setSalvo] = useState(edicao);
  const receberSalvo = async (dados: EdicaoDoAdmin) => {
    setSalvo(dados);
    await consultas.invalidateQueries({ queryKey: ['revista'] });
  };
  const salvar = useMutation({
    mutationFn: (valores: ValoresDaEdicao) => {
      const dados = {
        ...valores,
        publishedAt: valores.publishedAt?.toISOString() ?? null,
      };
      return salvo ? api.updateMagazineIssue(salvo.id, dados) : api.createMagazineIssue(dados);
    },
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('Edição salva.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const enviarCapa = useMutation({
    mutationFn: (arquivo: File) => api.uploadMagazineIssueCover(salvo!.id, arquivo),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('Capa enviada.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const ocupado = salvar.isPending || enviarCapa.isPending;
  return (
    <Modal
      open
      title={salvo ? 'Editar edição' : 'Nova edição'}
      onCancel={onFechar}
      destroyOnHidden
      footer={[
        <Button
          key="fechar"
          disabled={ocupado}
          onClick={onFechar}
        >
          Fechar
        </Button>,
        <Button
          key="salvar"
          type="primary"
          loading={salvar.isPending}
          disabled={ocupado}
          onClick={() => formulario.submit()}
        >
          Salvar
        </Button>,
      ]}
    >
      <Form
        form={formulario}
        layout="vertical"
        onFinish={(valores) => salvar.mutate(valores)}
        initialValues={{
          ...edicao,
          year: edicao?.year ?? dayjs().year(),
          publishedAt: edicao?.publishedAt ? dayjs(edicao.publishedAt) : null,
        }}
      >
        <Form.Item
          name="volume"
          label="Volume"
          rules={[{
            required: true,
            message: 'Informe o volume.',
          }]}
        >
          <InputNumber
            min={1}
            max={2147483647}
            precision={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="number"
          label="Número"
          rules={[{
            required: true,
            message: 'Informe o número.',
          }]}
        >
          <InputNumber
            min={1}
            max={2147483647}
            precision={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="year"
          label="Ano"
          rules={[{
            required: true,
            message: 'Informe o ano.',
          }]}
        >
          <InputNumber
            min={1990}
            max={2100}
            precision={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="publishedAt"
          label="Publicação"
          extra={
            'Vazio deixa em preparação. Data futura agenda. Data passada põe a edição no ar; ' +
            'cada artigo também precisa da própria data.'
          }
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <EnvioDeArquivo
          tipo="capa"
          salvo={Boolean(salvo)}
          temArquivo={salvo?.temCapa ?? false}
          enviando={enviarCapa.isPending}
          desabilitado={ocupado}
          onEnviar={(arquivo) => enviarCapa.mutate(arquivo)}
        />
      </Form>
    </Modal>
  );
}
